import React, { useState } from 'react';
import axios from 'axios';
import { Button, Tooltip } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GuidanceWindow from './GuidanceWindow';
import HcbsGuidanceSections from './HcbsGuidanceSections';
import FreeformGuidance from './FreeformGuidance';
import { APIURL } from '../../../config';

// The "Check Completeness" button plus the window it opens, as one drop-in unit.
//
// Every form-fill surface in the app renders its own <Form>, so without this the
// button would mean copying the state, the request and ~90 lines of result JSX
// into each of them. That is how the two shallow Form.io flatteners in
// container/Forms/ ended up as copies of each other, one of which was never
// fixed. One component, five call sites.
//
// Callers own the draft: <Form> is uncontrolled at most call sites, so they pass
// getSubmission() to read whatever the worker has typed at the moment the button
// is pressed.

/**
 * Reduces a Form.io submission to the plain answers, safe to serialise.
 *
 * Form.io's onChange hands back a live submission whose values can hold
 * references back to the form instance and its components -- so the object is
 * cyclic. Handing that to axios makes its deep config merge recurse until the
 * stack overflows ("Maximum call stack size exceeded" raised inside axios
 * utils, at the request call site rather than anywhere that looks like ours).
 * JSON.stringify would throw on the same input, so the cycle has to be broken
 * here rather than left to the serialiser.
 *
 * The API accepts either {data:{...}} or a bare answers object, so unwrapping
 * to the inner answers is the smaller, safer payload.
 */
function toPlainData(submission) {
    const raw = submission && typeof submission === 'object' && submission.data !== undefined
        ? submission.data
        : submission;
    if (!raw || typeof raw !== 'object') return {};

    const seen = new WeakSet();
    const strip = (value) => {
        if (value === null || typeof value !== 'object') return value;
        if (value instanceof Date) return value.toISOString();
        // A node reached twice is a cycle; dropping it loses no answer, because
        // an answer is a scalar, an array of scalars, or a plain object of them.
        if (seen.has(value)) return undefined;
        seen.add(value);
        if (Array.isArray(value)) return value.map(strip).filter((v) => v !== undefined);
        const out = {};
        for (const [key, val] of Object.entries(value)) {
            // Form.io hangs its own state off _-prefixed keys; they are not answers.
            if (key.startsWith('_') || key.startsWith('$')) continue;
            const stripped = strip(val);
            if (stripped !== undefined) out[key] = stripped;
        }
        return out;
    };

    const plain = strip(raw);
    return plain && typeof plain === 'object' ? plain : {};
}

/**
 * @param {number|string} formType     fw_forms.form_id of the definition being filled
 * @param {number|string} customerId   the client the form is about; the check reads
 *   their goals and needs, so the button is disabled without one
 * @param {number|string} [taskId]     the task the form was filled against; narrows the
 *   goals to that service type. Optional -- a form filled outside a task is checked
 *   against all of the client's active goals
 * @param {number|string} empId        picks which employee record to act as when the
 *   caller's email belongs to more than one company
 * @param {Function} getSubmission     () => the current Form.io submission
 * @param {boolean}  disabled
 * @param {string}   disabledReason    tooltip shown when disabled
 * @param {Object}   sx                overrides for the button
 */
const FormQualityCheck = ({
    formType,
    customerId,
    taskId,
    empId,
    getSubmission,
    disabled = false,
    disabledReason = '',
    sx = {},
}) => {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);

    // Goal alignment is the half of the check that needs the client's record, so
    // a form with no client attached cannot be evaluated the way the rest can.
    const missingCustomer = customerId === null || customerId === undefined || customerId === '';
    const isDisabled = disabled || loading || missingCustomer;
    const tooltip = missingCustomer
        ? 'Select a client first — the quality check compares the form against their goals.'
        : disabledReason;

    const checkQuality = async () => {
        setLoading(true);
        setError('');
        setResult(null);
        setOpen(true);
        try {
            const response = await axios.post(`${APIURL}/customer_form/guidance`, {
                form_type: formType,
                data: toPlainData(getSubmission ? getSubmission() : {}),
                customer_id: customerId,
                task_id: taskId ?? null,
                empId,
            });
            setResult(response.data);
        } catch (err) {
            console.error(err);
            // Distinguish the three cases that used to collapse into one string:
            // the API said no (has a message), the API was never reached, or the
            // request never left the browser.
            setError(
                err?.response?.data?.message
                || (err?.response
                    ? `The server returned ${err.response.status}. Please try again.`
                    : `Could not reach the server (${err?.message || 'unknown error'}).`),
            );
        } finally {
            setLoading(false);
        }
    };


    // A company with a custom prompt gets free-form text back (mode 'freeform'):
    // no score chip, no tips, no strategy sections -- just the text as written.
    const freeform = result?.mode === 'freeform';

    const button = (
        <Button
            variant="outlined"
            fullWidth
            sx={{
                padding: '8px',
                mt: 1,
                mb: 1,
                borderColor: '#5c6bc0',
                color: '#5c6bc0',
                '&:hover': {
                    borderColor: '#3949ab',
                    backgroundColor: '#e8eaf6',
                },
                ...sx,
            }}
            onClick={checkQuality}
            disabled={isDisabled}
            startIcon={<AutoFixHighIcon />}
        >
            {loading ? 'Checking...' : 'Check Completeness'}
        </Button>
    );

    return (
        <>
            {tooltip && isDisabled
                // A disabled MUI Button swallows pointer events, so the tooltip
                // needs a wrapper that still receives them.
                ? <Tooltip title={tooltip}><span>{button}</span></Tooltip>
                : button}

            <GuidanceWindow
                open={open}
                loading={loading}
                error={error}
                score={freeform ? undefined : result?.completeness_score}
                loadingText="Evaluating form completeness..."
                storageKey="formGuidanceWindowPosition"
                tips={freeform ? [] : (result?.guidance_tips || [])}
                hasResult={Boolean(result)}
                onClose={() => setOpen(false)}
            >
                {freeform
                    ? <FreeformGuidance text={result.text} />
                    : <HcbsGuidanceSections result={result} />}
            </GuidanceWindow>
        </>
    );
};

export default FormQualityCheck;
